import { Grid } from "@mui/material";
import SoftBox from "components/SoftBox";
import React, { useState, useEffect, useMemo } from "react";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import ReactCropModal from "../../ReactCropModal/ReactCropModal";
import HeaderContentSection from "./HeaderContentSection";
import HeroContentSection from "./HeroContentSection";
import HomePageHeadingsSection from "./HomePageHeadingsSection";
import HomePageHeadingsStylingSection from "./HomePageHeadingsStylingSection";
import PlaceholderCard from "./PlaceholderCard";
import FooterContentSection from "./FooterContentSection";
import AcknowledgmentContentSection from "./AcknowledgmentContentSection";
import SocialLinksSection from "./SocialLinksSection";
import CustomerInfoSection from "./CustomerInfoSection";
import FooterStylingSection from "./FooterStylingSection";
import ButtonStylingSection from "./ButtonStylingSection";
import SubmitButton from "./SubmitButton";
import useImageUpload from "./hooks/useImageUpload";
import useAcknowledgments from "./hooks/useAcknowledgments";
import useTagDropdown from "./hooks/useTagDropdown";

function Content({ handelThemeData, selectedTheme, buttonLoading }) {
  const contentData = selectedTheme?.theme_data?.content;
  const generalData = selectedTheme?.theme_data?.general;
  // Fallback to general data for social links and customer info if not in content (for migration)
  const inputData = {
    ...contentData,
    facebook: contentData?.facebook || generalData?.facebook,
    instagram: contentData?.instagram || generalData?.instagram,
    linkedin: contentData?.linkedin || generalData?.linkedin,
    twitter: contentData?.twitter || generalData?.twitter,
    youtube: contentData?.youtube || generalData?.youtube,
    contact_us: contentData?.contact_us || generalData?.contact_us,
    learn_more: contentData?.learn_more || generalData?.learn_more,
    email: contentData?.email || generalData?.email,
    name: contentData?.name || generalData?.name,
    phone_number: contentData?.phone_number || generalData?.phone_number,
    footer_font: contentData?.footer_font || generalData?.footer_font,
    footer_font_size: contentData?.footer_font_size || generalData?.footer_font_size,
    footer_background: contentData?.footer_background || generalData?.footer_background,
    footer_text: contentData?.footer_text || generalData?.footer_text,
    button_bg: contentData?.button_bg || generalData?.button_bg,
    button_text: contentData?.button_text || generalData?.button_text,
    button_border: contentData?.button_border || generalData?.button_border,
    button_font: contentData?.button_font || generalData?.button_font,
    button_font_size: contentData?.button_font_size || generalData?.button_font_size,
    featured_listings_per_page: contentData?.featured_listings_per_page || generalData?.featured_listings_per_page || "3",
    free_listings_per_page: contentData?.free_listings_per_page || generalData?.free_listings_per_page || "3",
  };
  const [agency] = useAgencyInfo();
  const services = useAppServices();

  const [showMap, setShowMap] = useState(inputData?.showMap || false);
  const [main_image, setMainImage] = useState(inputData?.main_image || "");
  const [img_1, setImg1] = useState(inputData?.img_1 || "");
  const [previewMainImage, setPreviewMainImage] = useState(main_image);
  const [previewImg1, setPreviewImg1] = useState(img_1);

  // Update previews when inputData changes
  useEffect(() => {
    if (inputData?.main_image) {
      setMainImage(inputData.main_image);
      setPreviewMainImage(inputData.main_image);
    }
    if (inputData?.img_1) {
      setImg1(inputData.img_1);
      setPreviewImg1(inputData.img_1);
    }
    if (inputData?.showMap !== undefined) {
      setShowMap(inputData.showMap);
    }
  }, [inputData]);

  // Custom hooks - useMemo to prevent recreating object on every render
  // Note: setter functions from useState are stable, so we don't need them in deps
  const imageSetters = useMemo(() => ({
    main_image: { setImage: setMainImage, setPreview: setPreviewMainImage },
    img_1: { setImage: setImg1, setPreview: setPreviewImg1 },
  }), []);

  const {
    cropModalOpen,
    currentImage,
    activeType,
    handleImageUpload,
    handleCropComplete,
    closeCropModal,
    getAspectRatio,
  } = useImageUpload(agency, imageSetters);

  const {
    acknowledgments,
    handleChange,
    handleChangeDnD,
    handleAdd,
    handleRemove,
  } = useAcknowledgments(contentData);

  const {
    openDropdownIndex,
    tagsData,
    loadingTag,
    tagInputs,
    handleToggle,
    handleSelectTag: handleSelectTagBase,
    handleInputChange: handleInputChangeBase,
  } = useTagDropdown(services, agency);

  const handleToggleChange = () => setShowMap((prev) => !prev);

  const handleSelectTag = (index, tag) => {
    handleSelectTagBase(index, tag, handleChange);
  };

  const handleInputChange = (index, value) => {
    handleInputChangeBase(index, value, handleChange);
  };

  const handleSubmit = async (e, val) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);

    const formObject = {};
    formData.forEach((value, key) => {
      formObject[key] = value;
    });

    formObject.showMap = showMap;
    formObject.acknowledgments = acknowledgments;
    if (val) formObject.extraValue = val;

    // Extract keys that should also be sent to general for backward compatibility
    const socialLinkKeys = ['facebook', 'instagram', 'linkedin', 'twitter', 'youtube', 'contact_us', 'learn_more'];
    const customerInfoKeys = ['email', 'name', 'phone_number'];
    const footerStylingKeys = ['footer_font', 'footer_font_size', 'footer_background', 'footer_text'];
    const buttonStylingKeys = ['button_bg', 'button_text', 'button_border', 'button_font', 'button_font_size'];
    
    const generalKeys = [...socialLinkKeys, ...customerInfoKeys, ...footerStylingKeys, ...buttonStylingKeys];
    const generalObject = {};
    generalKeys.forEach(key => {
      if (formObject[key] !== undefined) {
        generalObject[key] = formObject[key];
      }
    });

    handelThemeData({
      content: {
        ...formObject,
        img_1,
        main_image,
      },
      general: generalObject, // Also send to general for backward compatibility
    });
  };

  return (
    <div>
      <SoftBox component="form" onSubmit={(e) => handleSubmit(e, "someValue")}>
        <Grid container spacing={3} rowSpacing={2} paddingX={3} paddingBottom={4}>
          {/* Header */}
          <HeaderContentSection inputData={inputData} />

          {/* Hero Section */}
          <HeroContentSection 
            inputData={inputData} 
            showMap={showMap}
            handleToggleChange={handleToggleChange}
            main_image={main_image}
            previewMainImage={previewMainImage}
            handleImageUpload={handleImageUpload}
          />

          {/* Home Page Main Headings Content */}
          <HomePageHeadingsSection
            inputData={inputData}
            img_1={img_1}
            previewImg1={previewImg1}
            handleImageUpload={handleImageUpload}
          />

          {/* Placeholder Card (Left) */}
          <PlaceholderCard inputData={inputData} />

          {/* Home Page Main Headings Styling (Right) */}
          <HomePageHeadingsStylingSection inputData={inputData} />

          {/* Social & External Links */}
          <SocialLinksSection inputData={inputData} />

          {/* Customer Info */}
          <CustomerInfoSection inputData={inputData} />

          {/* Footer Content */}
          <FooterContentSection inputData={inputData} />

          {/* Button Styling */}
          <ButtonStylingSection inputData={inputData} />

          {/* Footer Styling */}
          <FooterStylingSection inputData={inputData} />

          {/* Acknowledgment Content */}
          <AcknowledgmentContentSection
            acknowledgments={acknowledgments}
            handleChange={handleChange}
            handleChangeDnD={handleChangeDnD}
            handleAdd={handleAdd}
            handleRemove={handleRemove}
            openDropdownIndex={openDropdownIndex}
            handleToggle={handleToggle}
            handleSelectTag={handleSelectTag}
            handleInputChange={handleInputChange}
            tagInputs={tagInputs}
            tagsData={tagsData}
            loadingTag={loadingTag}
          />

          <SubmitButton buttonLoading={buttonLoading} />
        </Grid>
      </SoftBox>

      <ReactCropModal
        open={cropModalOpen}
        onClose={closeCropModal}
        currentImage={currentImage}
        onCropComplete={handleCropComplete}
        aspectRatio={getAspectRatio()}
      />
    </div>
  );
}

export default Content;
